import { useEffect, useCallback } from "react";
import { debounce } from "lodash";
import cursorPositionStore from "@app/state/store/editor/cursor-position";

/**
 * Custom hook to track and save cursor position and scroll state in the editor
 * @param {object} editor - TipTap editor instance
 * @param {string} projectId - Current project ID
 * @param {string} sectionId - Current section ID
 * @param {boolean} readonly - Whether editor is in readonly mode
 * @returns {void}
 */
export function useCursorPositionTracking(editor, projectId, sectionId, readonly) {
    const savePosition = useCallback(
        debounce((editorInstance) => {
            if (
                !editorInstance ||
                readonly ||
                !projectId ||
                !sectionId ||
                editorInstance.isDestroyed
            ) {
                return;
            }

            try {
                const { selection } = editorInstance.state;
                const cursorFrom = selection.from;
                const cursorTo = selection.to;
                const scrollY = window.scrollY || 0;

                const content = editorInstance.getJSON();
                cursorPositionStore.saveCursorPosition(
                    projectId,
                    sectionId,
                    cursorFrom,
                    cursorTo,
                    scrollY,
                    content,
                );
            } catch (e) {
                console.warn("Failed to save cursor position:", e);
            }
        }, 500),
        [projectId, sectionId, readonly],
    );

    useEffect(() => {
        if (!editor || readonly || !projectId || !sectionId) {
            return;
        }

        const handleSelectionUpdate = () => {
            savePosition(editor);
        };

        editor.on("selectionUpdate", handleSelectionUpdate);
        editor.on("update", handleSelectionUpdate);

        return () => {
            editor.off("selectionUpdate", handleSelectionUpdate);
            editor.off("update", handleSelectionUpdate);
            savePosition.cancel();
        };
    }, [editor, readonly, projectId, sectionId, savePosition]);
}
